/**
 * Utility to generate appropriate polling paramters for different Poller types
 */
import {
  FireTVCampaignAutomationClient,
  GetTransformedAssetCommandOutput,
  TransformationStatus,
} from '@amzn/fire-tv-campaign-automation-service';
import { ErrorConstants, Poller } from 'src/constants';
import { ToastContextType } from 'src/contexts/ToastContext';
import { PollingMaxRetriesError, PollingTimeoutError } from 'src/errors/CustomErrors';
import { logger } from 'src/logger';
import { AssetStatus } from 'src/types';

import { getTransformedAsset } from './clients/controlPlane/apiMethods';
import { PollerOptions } from './Poller';

// Constants for Transformation Polling
const TRANSFORMATION_INTERVAL_SECONDS = 10;
const TRANSFORMATION_MAX_RETRIES = 10;
const TRANSFORMATION_TIMEOUT_SECONDS = 120;

const TRANSFORMATION_TERMINAL_STATES: TransformationStatus[] = [
  TransformationStatus.TRANSFORMATION_COMPLETE,
  TransformationStatus.TRANSFORMATION_FAILED,
];

export const ASSET_TRANSFORMATION_STATUS_MESSAGES = {
  error: 'Error transforming asset, please try again in some time',
  pollingFailed: 'Unable to get the transformed asset. Please try again later.',
  pollingTimeout: 'Asset transformation is taking longer than expected. Please try again later.',
  maxRetriesExceeded:
    'Unable to get the transformed asset after multiple attempts. Please try again later.',
};

/**
 *
 */
export function getPollingOperation(
  poller: Poller,
  controlPlaneClient: FireTVCampaignAutomationClient,
  id: string,
) {
  switch (poller) {
    case Poller.Transformation:
    default:
      return () => getTransformedAsset(controlPlaneClient, id);
  }
}

/**
 * Creates PollerOptions for the specified poller type
 * @param poller - The type of poller to create options for
 * @param setState - Function to update state based on polling results
 * @param toast - Toast context for displaying notifications
 * @param abortSignal - Optional signal to abort polling
 * @returns Complete PollerOptions configuration
 */
export function getPollerOptions<T, S = unknown>(
  poller: Poller,
  setState: (state: Partial<S>) => void,
  toast: ToastContextType,
  abortSignal?: AbortSignal,
): PollerOptions<T> {
  return {
    ...getPollingTimingOptions(poller),
    abortSignal,
    successCondition: getSuccessCondition(poller),
    onError: getOnError(poller),
    onSuccess: getOnSuccess(poller, setState, toast),
  };
}

/**
 * Creates terminal error handler for the specified poller type
 * @param poller - The poller type
 * @param setState - Function to update state
 * @param toast - Toast context for notifications
 * @returns Terminal error handler function
 */
export function getOnTerminalErrorHandler<S>(
  poller: Poller,
  setState: (state: Partial<S>) => void,
  toast: ToastContextType,
) {
  switch (poller) {
    case Poller.Transformation:
    default:
      return (error: unknown) => {
        let description: string;
        switch (true) {
          case error instanceof PollingTimeoutError:
            description = ASSET_TRANSFORMATION_STATUS_MESSAGES.pollingTimeout;
            break;
          case error instanceof PollingMaxRetriesError:
            description = ASSET_TRANSFORMATION_STATUS_MESSAGES.maxRetriesExceeded;
            break;
          default:
            description = ASSET_TRANSFORMATION_STATUS_MESSAGES.pollingFailed;
        }
        setState({
          status: AssetStatus.FAILED,
          error: ErrorConstants.MESSAGES.TRANSFORMATION_ERROR,
          key: undefined,
        } as unknown as Partial<S>);
        toast.addToast({
          variant: 'danger',
          description,
          persistent: true,
        });
      };
  }
}

/**
 * Gets timing configuration for the specified poller type
 * @param poller - The poller type
 * @returns Object with intervalSeconds, maxRetries, and timeoutSeconds
 */
function getPollingTimingOptions(poller: Poller) {
  switch (poller) {
    case Poller.Transformation:
    default:
      return {
        intervalSeconds: TRANSFORMATION_INTERVAL_SECONDS,
        maxRetries: TRANSFORMATION_MAX_RETRIES,
        timeoutSeconds: TRANSFORMATION_TIMEOUT_SECONDS,
      };
  }
}

/**
 * Creates success callback for the specified poller type
 * @param poller - The poller type
 * @param setState - Function to update state
 * @param toast - Optional toast context for notifications
 * @returns Success callback function
 */
function getOnSuccess<T, S>(
  poller: Poller,
  setState: (state: Partial<S>) => void,
  toast?: ToastContextType,
) {
  switch (poller) {
    case Poller.Transformation:
    default:
      return (result: T) => {
        const transformResult = result as unknown as GetTransformedAssetCommandOutput;
        logger.debug('Transformation status polling succeeded', { result });
        if (transformResult.status === TransformationStatus.TRANSFORMATION_COMPLETE) {
          setState({
            status: AssetStatus.COMPLETED,
            key: transformResult.transformedAssetKey,
          } as unknown as Partial<S>);
        } else if (transformResult.status === TransformationStatus.TRANSFORMATION_FAILED) {
          setState({
            status: AssetStatus.FAILED,
            error: ErrorConstants.MESSAGES.TRANSFORMATION_ERROR,
            key: undefined,
          } as unknown as Partial<S>);
          toast?.addToast({
            variant: 'danger',
            description: ASSET_TRANSFORMATION_STATUS_MESSAGES.error,
            persistent: true,
          });
        }
      };
  }
}

/**
 * Creates success condition function for the specified poller type
 * @param poller - The poller type
 * @returns Function that determines if polling should stop based on result
 */
function getSuccessCondition(poller: Poller) {
  switch (poller) {
    case Poller.Transformation:
    default:
      return (result: unknown) => {
        const transformResult = result as GetTransformedAssetCommandOutput;
        return TRANSFORMATION_TERMINAL_STATES.includes(transformResult.status);
      };
  }
}

/**
 * Creates error callback for the specified poller type
 * @param poller - The poller type
 * @returns Error callback function
 */
function getOnError(poller: Poller) {
  switch (poller) {
    case Poller.Transformation:
    default:
      return (error: Error) => logger.warn('Transformation polling attempt failed', { error });
  }
}
